import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import avatar from '../assets/images/prince-profile.png';
import { Link } from 'react-router-dom';
import { FiDownload, FiMail } from 'react-icons/fi';
import api, { getAssetUrl } from '../utils/api';
import { useState, useEffect } from 'react';

// Components
import SkillBar from '../components/skills/SkillBar';
import PageTransition from '../components/ui/PageTransition';
import { useGSAPAnimations } from '../hooks/useGSAPAnimations';

const About = () => {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  useGSAPAnimations();

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const [skills, setSkills] = useState([
    { name: 'HTML/CSS', percentage: 90 },
    { name: 'JavaScript', percentage: 85 },
    { name: 'C/C++', percentage: 95 },
    { name: 'Python', percentage: 85 },
    { name: 'MySQL', percentage: 80 },
    { name: 'MongoDB', percentage: 75 },
    { name: 'Node.js', percentage: 70 },
    { name: 'React', percentage: 75 },
  ]);

  const [experiences, setExperiences] = useState([]);
  const [education, setEducation] = useState([]);
  const [resumeUrl, setResumeUrl] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [timelineRes, skillsRes] = await Promise.all([
          api.get('/timeline'),
          api.get('/skills')
        ]);

        const timeline = timelineRes.data;
        setExperiences(timeline.filter(t => t.type === 'experience').sort((a, b) => (a.order || 0) - (b.order || 0)));
        setEducation(timeline.filter(t => t.type === 'education').sort((a, b) => (a.order || 0) - (b.order || 0)));

        if (skillsRes.data.length > 0) {
          setSkills(skillsRes.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
        }

        // Fetch config for resume
        const configRes = await api.get('/config');
        const resume = configRes.data.find(c => c.key === 'resumeUrl');
        if (resume) setResumeUrl(resume.value);
      } catch (err) {
        console.error('Error fetching about data:', err);
      }
    };
    fetchData();
  }, []);

  return (
    <PageTransition>
      <AboutContainer>
        <PageHeader>
          <div className="container">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              About Me
            </motion.h1>
            <motion.div
              className="breadcrumbs"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link to="/">Home</Link> / <span>About</span>
            </motion.div>
          </div>
        </PageHeader>

        <Section>
          <div className="container">
            <AboutGrid>
              <motion.div
                className="about-image"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ProfileImage>
                  <img src={avatar} alt="Prince Yadav" />
                </ProfileImage>
              </motion.div>

              <motion.div
                className="about-content"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={staggerContainer}
              >
                <motion.h2 variants={fadeInUp}>My Story</motion.h2>
                <motion.p variants={fadeInUp}>
                  Hello! I'm a dedicated and versatile Web developer with a passion for creating innovative and efficient solutions. With expertise in both web and application development, I specialize in building dynamic, user-friendly websites and mobile apps.
                </motion.p>
                <motion.p variants={fadeInUp}>
                  My skill set spans a wide range of technologies, including front-end and back-end development, databases, and cloud-based services. I'm currently pursuing my Bachelor of Technology in Computer Science and Engineering with a specialization in Cyber Security at Shoolini University.
                </motion.p>
                <motion.p variants={fadeInUp}>
                  I am committed to continuous learning, staying up-to-date with the latest advancements in software engineering, and delivering high-quality code that meets business objectives and enhances user experiences.
                </motion.p>

                <motion.div className="personal-info" variants={fadeInUp}>
                  <InfoGrid>
                    <InfoItem>
                      <strong>Name:</strong> Prince Yadav
                    </InfoItem>
                    <InfoItem>
                      <strong>Email:</strong> princeyadav3033@gmail.com
                    </InfoItem>
                    <InfoItem>
                      <strong>Phone:</strong> 8787043175
                    </InfoItem>
                    <InfoItem>
                      <strong>Location:</strong> Delhi, India
                    </InfoItem>
                    <InfoItem>
                      <strong>Availability:</strong> Freelance / Part-time
                    </InfoItem>
                    <InfoItem>
                      <strong>Languages:</strong> English, Hindi
                    </InfoItem>
                  </InfoGrid>
                </motion.div>

                <motion.div className="cta-buttons" variants={fadeInUp}>
                  <DownloadButton href={getAssetUrl(resumeUrl) || '#'} target="_blank" rel="noopener noreferrer">
                    <FiDownload /> Download Resume
                  </DownloadButton>
                  <ContactButton to="/contact">
                    <FiMail /> Contact Me
                  </ContactButton>
                </motion.div>
              </motion.div>
            </AboutGrid>
          </div>
        </Section>

        <Section className="bg-alt">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                My Skills
              </motion.h2>
            </SectionTitle>

            <SkillsGrid>
              {skills.map((skill, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeInUp}
                >
                  <SkillBar name={skill.name} percentage={skill.percentage} />
                </motion.div>
              ))}
            </SkillsGrid>
          </div>
        </Section>

        <Section>
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                Experience
              </motion.h2>
            </SectionTitle>

            <TimelineContainer>
              {experiences.map((exp) => (
                <motion.div
                  className="timeline-item"
                  key={exp.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeInUp}
                >
                  <TimelineItem>
                    <TimelineDot />
                    <TimelineContent>
                      <TimelinePeriod>{exp.period}</TimelinePeriod>
                      <h3>{exp.position}</h3>
                      <h4>{exp.company}</h4>
                      <p>{exp.description}</p>
                    </TimelineContent>
                  </TimelineItem>
                </motion.div>
              ))}
            </TimelineContainer>
          </div>
        </Section>

        <Section className="bg-alt">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                Education
              </motion.h2>
            </SectionTitle>

            <TimelineContainer>
              {education.map((edu) => (
                <motion.div
                  className="timeline-item"
                  key={edu.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeInUp}
                >
                  <TimelineItem>
                    <TimelineDot />
                    <TimelineContent>
                      <TimelinePeriod>{edu.period}</TimelinePeriod>
                      <h3>{edu.degree || edu.position}</h3>
                      <h4>{edu.institution || edu.company}</h4>
                      <p>{edu.description}</p>
                    </TimelineContent>
                  </TimelineItem>
                </motion.div>
              ))}
            </TimelineContainer>
          </div>
        </Section>
      </AboutContainer>
    </PageTransition>
  );
};

const AboutContainer = styled.div`
  width: 100%;
`;

const PageHeader = styled.header`
  padding: 8rem 0 4rem;
  text-align: center;
  position: relative;
  z-index: 1;
  background-color: transparent;
  
  .light & {
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  .dark & {
    background-color: rgba(0, 0, 0, 0.2);
  }
  
  h1 {
    margin-bottom: 1rem;
  }
  
  .breadcrumbs {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    
    a {
      .light & {
        color: var(--primary-light);
      }
      
      .dark & {
        color: var(--primary-dark);
      }
    }
    
    span {
      .light & {
        color: var(--text-secondary-light);
      }
      
      .dark & {
        color: var(--text-secondary-dark);
      }
    }
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;
  z-index: 1;
  
  &.bg-alt {
    .light & {
      background-color: rgba(255, 255, 255, 0.03);
    }
    
    .dark & {
      background-color: rgba(0, 0, 0, 0.15);
    }
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
  
  .about-content {
    h2 {
      font-size: 2.5rem;
      margin-bottom: 1.5rem;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        width: 50px;
        height: 3px;
        background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
        
        .dark & {
          background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
        }
      }
    }
    
    p {
      margin-bottom: 1.5rem;
      
      .light & {
        color: var(--text-secondary-light);
      }
      
      .dark & {
        color: var(--text-secondary-dark);
      }
    }
    
    .personal-info {
      margin: 2rem 0;
    }
    
    .cta-buttons {
      display: flex;
      gap: 1rem;
      margin-top: 2rem;
      
      @media (max-width: 480px) {
        flex-direction: column;
      }
    }
  }
`;

const ProfileImage = styled.div`
  position: relative;
  border-radius: var(--border-radius);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: 20px;
    bottom: 20px;
    border: 3px solid var(--primary-light);
    border-radius: var(--border-radius);
    z-index: -1;
    
    .dark & {
      border-color: var(--primary-dark);
    }
  }
  
  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: var(--border-radius);
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  strong {
    margin-right: 0.5rem;
    
    .light & {
      color: var(--text-primary-light);
    }
    
    .dark & {
      color: var(--text-primary-dark);
    }
  }
  
  .light & {
    color: var(--text-secondary-light);
  }
  
  .dark & {
    color: var(--text-secondary-dark);
  }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
  color: white;
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  .dark & {
    background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
  }
`;

const ContactButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: transparent;
  border: 2px solid var(--primary-light);
  color: var(--primary-light);
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-light);
    color: white;
  }
  
  .dark & {
    border-color: var(--primary-dark);
    color: var(--primary-dark);
    
    &:hover {
      background-color: var(--primary-dark);
      color: var(--bg-primary-dark);
    }
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    display: inline-block;
    position: relative;
    font-size: 2.5rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
      
      .dark & {
        background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
      }
    }
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 20px;
    width: 2px;
    background: linear-gradient(to bottom, var(--primary-light), var(--secondary-light));
    
    .dark & {
      background: linear-gradient(to bottom, var(--primary-dark), var(--secondary-dark));
    }
    
    @media (max-width: 480px) {
      left: 15px;
    }
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-left: 60px;
  margin-bottom: 3rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 480px) {
    padding-left: 45px;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  top: 5px;
  left: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
  
  .dark & {
    background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
  }
  
  @media (max-width: 480px) {
    left: 5px;
    width: 18px;
    height: 18px;
  }
`;

const TimelineContent = styled.div`
  padding: 1.5rem;
  border-radius: var(--border-radius);
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
  -webkit-backdrop-filter: blur(var(--glass-blur));
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  h4 {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    font-weight: 500;
    
    .light & {
      color: var(--primary-light);
    }
    
    .dark & {
      color: var(--primary-dark);
    }
  }
  
  p {
    .light & {
      color: var(--text-secondary-light);
    }
    
    .dark & {
      color: var(--text-secondary-dark);
    }
  }
`;

const TimelinePeriod = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 1rem;
  
  .light & {
    background-color: var(--bg-secondary-light);
    color: var(--primary-light);
  }
  
  .dark & {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--primary-dark);
  }
`;

export default About;